/**
 * Server ↔ Square Team Member mapping card (feat-039 F2).
 *
 * Lists the Square Team Members (POS employees) for the connected merchant and
 * lets an operator map each to a Bespoke `server` row. Backed by the
 * `server_pos_mapping` CRUD endpoints (ADR 0005 D1 — composite key
 * (source, vendor_employee_id) → internal_server_id, tenant-scoped).
 *
 * Once a Team Member is mapped, that vendor employee's orders attribute to the
 * internal server without the `_extract_employee_name` string fallback (B5).
 *
 * Hosted on LocationAdminView alongside SquareConnectionCard.
 * RBAC: viewable by Admin/Manager/Corporate (StaffRoute upstream); the
 * map/remove write actions require Admin or Corporate (`canManage`).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Loader2, RefreshCw, Trash2, Users } from 'lucide-react';
import { config } from '../../../config/environment';
import managementService from '../../../services/managementService';
import {
  PosEmployee,
  listPosEmployees,
  removeServerMapping,
  setServerMapping,
} from '../../../services/posMatchingService';

interface Props {
  tenantId: string;
  bespokeLocationId: string;
  /** Whether the current user can map / remove (Admin or Corporate). */
  canManage: boolean;
}

interface ServerOption {
  id: string;
  name: string;
}

function normalizeServer(raw: any): ServerOption | null {
  const id = String(raw?.id ?? raw?.server_id ?? raw?.server_shift_id ?? '');
  if (!id) return null;
  const name =
    raw?.name ??
    raw?.server_name ??
    [raw?.first_name, raw?.last_name].filter(Boolean).join(' ') ??
    id;
  return { id, name: String(name || id) };
}

const FAKE_SERVERS: ServerOption[] = [
  { id: 'srv-1', name: 'Jane K.' },
  { id: 'srv-2', name: 'Devon L.' },
  { id: 'srv-3', name: 'Priya N.' },
  { id: 'srv-4', name: 'Sam T.' },
];

const PosEmployeeMappingCard: React.FC<Props> = ({ tenantId, bespokeLocationId, canManage }) => {
  const [employees, setEmployees] = useState<PosEmployee[] | null>(null);
  const [servers, setServers] = useState<ServerOption[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Per-row pending selection (vendor_employee_id -> chosen server id, not yet saved).
  const [selection, setSelection] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    // GET /pos/employees is Admin/Corporate-only server-side. For Managers we
    // skip the fetch entirely and render a graceful restricted notice rather
    // than letting the call 403 into an error card.
    if (!canManage) {
      setEmployees([]);
      return;
    }
    setLoadError(null);
    try {
      const [emps, serverResp] = await Promise.all([
        listPosEmployees(tenantId, 'square'),
        managementService.getServers(bespokeLocationId, tenantId),
      ]);
      setEmployees(emps);
      let opts = (serverResp.servers ?? [])
        .map(normalizeServer)
        .filter((s): s is ServerOption => s !== null);
      if (opts.length === 0 && config.environment === 'fake') opts = FAKE_SERVERS;
      setServers(opts);
    } catch (e: any) {
      console.error('PosEmployeeMappingCard - load failed', e);
      setLoadError(e?.message ?? 'Failed to load Team Members');
    }
  }, [tenantId, bespokeLocationId, canManage]);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async (emp: PosEmployee) => {
    const chosen = selection[emp.vendor_employee_id];
    if (!chosen) return;
    setBusyId(emp.vendor_employee_id);
    setFlash(null);
    try {
      await setServerMapping(tenantId, {
        source: 'square',
        vendor_employee_id: emp.vendor_employee_id,
        internal_server_id: chosen,
      });
      const serverName = servers.find((s) => s.id === chosen)?.name ?? chosen;
      setFlash({ kind: 'success', text: `${emp.display_name} mapped to ${serverName}.` });
      // Optimistic local update so the row reflects the mapping without a full refetch.
      setEmployees((prev) =>
        (prev ?? []).map((e) =>
          e.vendor_employee_id === emp.vendor_employee_id
            ? { ...e, mapped_internal_server_id: chosen, mapped_server_name: serverName }
            : e,
        ),
      );
      setSelection((prev) => {
        const next = { ...prev };
        delete next[emp.vendor_employee_id];
        return next;
      });
    } catch (e: any) {
      setFlash({ kind: 'error', text: e?.message ?? 'Failed to save mapping' });
    } finally {
      setBusyId(null);
    }
  };

  const onRemove = async (emp: PosEmployee) => {
    setBusyId(emp.vendor_employee_id);
    setFlash(null);
    try {
      await removeServerMapping(tenantId, 'square', emp.vendor_employee_id);
      setFlash({ kind: 'success', text: `Mapping removed for ${emp.display_name}.` });
      setEmployees((prev) =>
        (prev ?? []).map((e) =>
          e.vendor_employee_id === emp.vendor_employee_id
            ? { ...e, mapped_internal_server_id: null, mapped_server_name: null }
            : e,
        ),
      );
    } catch (e: any) {
      setFlash({ kind: 'error', text: e?.message ?? 'Failed to remove mapping' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section
      data-testid="pos-employee-mapping-card"
      className="bg-white border border-softBorder rounded-xl shadow-sm overflow-hidden mt-6"
    >
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-softBorder bg-cream/40">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-coral to-gold flex items-center justify-center text-white" aria-hidden>
            <Users className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-lg font-display font-semibold text-ink">Square staff mapping</h2>
            <p className="text-xs text-inkMuted">
              Link each Square Team Member to a Bespoke server so their orders attribute correctly.
            </p>
          </div>
        </div>
        {canManage && (
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 border border-softBorder rounded bg-white hover:bg-cream"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        )}
      </div>

      {flash && (
        <div
          role="status"
          className={`mx-5 mt-4 border rounded px-3 py-2 text-sm ${
            flash.kind === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
              : 'bg-rose-50 border-rose-200 text-rose-900'
          }`}
        >
          {flash.text}
        </div>
      )}

      <div className="p-5">
        {!canManage && (
          <div data-testid="pos-employee-mapping-restricted" className="rounded border border-softBorder bg-cream/40 p-3 text-sm">
            <p className="text-ink font-medium">Managed by Admin or Corporate</p>
            <p className="text-xs text-inkMuted mt-1">
              Square Team Member ↔ server mappings are set by Admin or Corporate users. Ask an
              administrator to map staff for this location.
            </p>
          </div>
        )}

        {canManage && loadError && (
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium">We couldn't load Square Team Members.</p>
              <p className="text-xs mt-0.5 opacity-80">{loadError}</p>
            </div>
            <button onClick={load} className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 border border-amber-300 rounded bg-white hover:bg-amber-100">
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
          </div>
        )}

        {canManage && !loadError && employees === null && (
          <div className="flex items-center gap-2 text-sm text-inkMuted">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading Team Members…
          </div>
        )}

        {canManage && !loadError && employees !== null && employees.length === 0 && (
          <div data-testid="pos-employee-mapping-empty" className="rounded border border-softBorder bg-cream/40 p-3 text-sm">
            <p className="text-ink font-medium">No Square Team Members yet</p>
            <p className="text-xs text-inkMuted mt-1">
              Team Members appear here after Square is connected and the staff sync runs. If you just
              connected, check back in a few minutes.
            </p>
          </div>
        )}

        {canManage && !loadError && employees !== null && employees.length > 0 && (
          <ul className="divide-y divide-softBorder border border-softBorder rounded">
            {employees.map((emp) => {
              const mapped = !!emp.mapped_internal_server_id;
              const rowBusy = busyId === emp.vendor_employee_id;
              const chosen = selection[emp.vendor_employee_id] ?? '';
              return (
                <li
                  key={emp.vendor_employee_id}
                  data-testid="pos-employee-row"
                  className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:items-center px-3 py-2.5 text-sm"
                >
                  <div className="sm:col-span-4 min-w-0">
                    <p className="font-medium text-ink truncate">{emp.display_name}</p>
                    <p className="text-xs text-inkMuted truncate">{emp.vendor_employee_id}</p>
                  </div>

                  <div className="sm:col-span-3">
                    {mapped ? (
                      <span data-testid="pos-employee-mapped-label" className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
                        <Check className="w-3.5 h-3.5" />
                        {emp.mapped_server_name ?? emp.mapped_internal_server_id}
                      </span>
                    ) : (
                      <span className="text-xs text-inkMuted">Unmapped</span>
                    )}
                  </div>

                  <div className="sm:col-span-5 flex items-center gap-2">
                    <select
                      data-testid="pos-employee-server-select"
                      value={chosen}
                      disabled={!canManage || rowBusy}
                      onChange={(e) =>
                        setSelection((prev) => ({ ...prev, [emp.vendor_employee_id]: e.target.value }))
                      }
                      className="flex-1 border border-softBorder rounded px-2 py-1.5 text-sm bg-white disabled:bg-cream"
                    >
                      <option value="">{mapped ? 'Change to…' : 'Map to server…'}</option>
                      {servers.map((s) => (
                        <option key={s.id} value={s.id} disabled={s.id === emp.mapped_internal_server_id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                    <button
                      data-testid="pos-employee-save-button"
                      onClick={() => onSave(emp)}
                      disabled={!canManage || rowBusy || !chosen}
                      className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 bg-coral text-white rounded hover:bg-coral/90 disabled:opacity-50"
                    >
                      {rowBusy && chosen ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                      Save
                    </button>
                    {mapped && canManage && (
                      <button
                        data-testid="pos-employee-remove-button"
                        onClick={() => onRemove(emp)}
                        disabled={rowBusy}
                        aria-label={`Remove mapping for ${emp.display_name}`}
                        className="inline-flex items-center justify-center p-1.5 border border-softBorder rounded bg-white hover:bg-cream text-rose-700 disabled:opacity-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

      </div>
    </section>
  );
};

export default PosEmployeeMappingCard;
